<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import { db } from '@/firebase'
import { useOpsStore } from '@/stores/ops'
import { useCommsStore } from '@/stores/comms'
import { useAuthStore } from '@/stores/auth'
import FosSelect from '@/components/ui/FosSelect.vue'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'
import FosDialog from '@/components/ui/FosDialog.vue'
import { formatPhone } from '@/utils/phone'
import type { AgencyChannelPreset, CommsChannel } from '@/types'

const props = defineProps<{
  incidentId: string
  opId: string
}>()

const opsStore = useOpsStore()
const commsStore = useCommsStore()
const auth = useAuthStore()

const ASSIGNMENTS = ['Command', 'Tactical', 'Air-to-Ground'] as const

const ASSIGNMENT_DOT: Record<string, string> = {
  Command: 'bg-primary',
  Tactical: 'bg-green-500',
  'Air-to-Ground': 'bg-violet-500',
}

const ASSIGNMENT_TEXT: Record<string, string> = {
  Command: 'text-primary',
  Tactical: 'text-green-700 dark:text-green-400',
  'Air-to-Ground': 'text-violet-700 dark:text-violet-400',
}

const MODE_OPTIONS = [
  { label: 'Analog', value: 'A' },
  { label: 'Digital', value: 'D' },
  { label: 'Mixed', value: 'M' },
]
const MODE_LABELS: Record<string, string> = { A: 'Analog', D: 'Digital', M: 'Mixed' }
const MODE_ORDER = ['A', 'D', 'M']

// ── ICP Phone ─────────────────────────────────────────────────────────────────
const phoneInputRef = ref<HTMLInputElement | null>(null)

function onPhoneInput(e: Event) {
  const target = e.target as HTMLInputElement
  const cursorPos = target.selectionStart ?? target.value.length
  const digitsBeforeCursor = (target.value.slice(0, cursorPos).match(/\d/g) ?? []).length
  const formatted = formatPhone(target.value)
  target.value = formatted
  let newPos = 0
  let digitsSeen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i] ?? '')) {
      digitsSeen++
      if (digitsSeen === digitsBeforeCursor) { newPos = i + 1; break }
    }
  }
  if (digitsSeen < digitsBeforeCursor) newPos = formatted.length
  target.setSelectionRange(newPos, newPos)
}

async function savePhone() {
  if (!phoneInputRef.value) return
  const value = phoneInputRef.value.value.trim() || null
  const current = opsStore.selectedOp?.contact_phone ?? null
  if (value !== current) {
    await opsStore.updateOp(props.incidentId, props.opId, { contact_phone: value })
  }
}

// ── Agency presets ─────────────────────────────────────────────────────────────
const agencyPresets = ref<AgencyChannelPreset[]>([])
let presetsUnsub: Unsubscribe | null = null

onMounted(() => {
  const agencyId = auth.userProfile?.agency_id
  if (!agencyId) return
  presetsUnsub = onSnapshot(collection(db, 'agencies', agencyId, 'channel_presets'), (snap) => {
    agencyPresets.value = snap.docs
      .map((d) => ({ preset_id: d.id, ...d.data() }) as AgencyChannelPreset)
      .sort((a, b) => a.order - b.order)
  })
})

onUnmounted(() => {
  presetsUnsub?.()
})

const presetsByMode = computed(() => {
  const groups: { mode: string; label: string; presets: AgencyChannelPreset[] }[] = []
  for (const mode of MODE_ORDER) {
    const presets = agencyPresets.value.filter((p) => p.mode === mode)
    if (presets.length) groups.push({ mode, label: MODE_LABELS[mode] ?? mode, presets })
  }
  const known = new Set(MODE_ORDER)
  const others = agencyPresets.value.filter((p) => !known.has(p.mode))
  if (others.length) groups.push({ mode: 'other', label: 'Other', presets: others })
  return groups
})

// ── Add dialog ────────────────────────────────────────────────────────────────
const addDialogOpen = ref(false)
const addShowCustomForm = ref(false)
const addAssignment = ref<string>('Command')

const addForm = ref({
  name: '',
  assignment: 'Command',
  rx_freq: '',
  rx_tone: '',
  tx_freq: '',
  tx_tone: '',
  mode: 'A',
  remarks: '',
})

function openAddDialog() {
  addAssignment.value = 'Command'
  addShowCustomForm.value = false
  addDialogOpen.value = true
}

function closeAddDialog() {
  addDialogOpen.value = false
  addShowCustomForm.value = false
}

function openAddCustomForm() {
  addForm.value = {
    name: '',
    assignment: addAssignment.value,
    rx_freq: '',
    rx_tone: '',
    tx_freq: '',
    tx_tone: '',
    mode: 'A',
    remarks: '',
  }
  addShowCustomForm.value = true
}

async function addFromPreset(preset: AgencyChannelPreset) {
  await commsStore.addChannel(props.incidentId, props.opId, {
    name: preset.name,
    assignment: addAssignment.value,
    rx_freq: preset.rx_freq,
    rx_tone: preset.rx_tone,
    tx_freq: preset.tx_freq,
    tx_tone: preset.tx_tone,
    mode: preset.mode,
    remarks: preset.remarks,
  })
  closeAddDialog()
}

async function submitAddCustom() {
  if (!addForm.value.name.trim()) return
  await commsStore.addChannel(props.incidentId, props.opId, {
    ...addForm.value,
    assignment: addAssignment.value,
  })
  closeAddDialog()
}

const addDialogTitle = computed(() =>
  addShowCustomForm.value ? `Custom channel — ${addAssignment.value}` : 'Add channel',
)

// ── Edit dialog ───────────────────────────────────────────────────────────────
const editDialogOpen = ref(false)
const editChannelId = ref<string | null>(null)

const editForm = ref({
  name: '',
  assignment: 'Command',
  rx_freq: '',
  rx_tone: '',
  tx_freq: '',
  tx_tone: '',
  mode: 'A',
  remarks: '',
})

function openEditDialog(channel: CommsChannel) {
  editChannelId.value = channel.channel_id
  editForm.value = {
    name: channel.name,
    assignment: channel.assignment,
    rx_freq: channel.rx_freq,
    rx_tone: channel.rx_tone,
    tx_freq: channel.tx_freq,
    tx_tone: channel.tx_tone,
    mode: channel.mode,
    remarks: channel.remarks,
  }
  editDialogOpen.value = true
}

function closeEditDialog() {
  editDialogOpen.value = false
  editChannelId.value = null
}

async function saveEdit() {
  if (!editChannelId.value || !editForm.value.name.trim()) return
  await commsStore.updateChannel(props.incidentId, props.opId, editChannelId.value, {
    ...editForm.value,
  })
  closeEditDialog()
}

async function deleteEditChannel() {
  if (!editChannelId.value) return
  await commsStore.removeChannel(props.incidentId, props.opId, editChannelId.value)
  closeEditDialog()
}
</script>

<template>
  <div class="mx-6 mt-4 bg-surface border border-border rounded p-3 shrink-0 flex gap-5">
    <!-- Radio Channels -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center justify-between mb-1.5">
        <p class="text-[9px] font-label uppercase tracking-widest text-outline">Radio Channels</p>
        <button
          type="button"
          class="flex items-center gap-0.5 text-[9px] font-mono text-outline hover:text-on-surface transition-colors"
          @click="openAddDialog"
        >
          <span class="material-symbols-outlined text-[11px]">add</span>
          Add
        </button>
      </div>

      <!-- Chips -->
      <div v-if="commsStore.channels.length" class="flex flex-wrap gap-1.5">
        <button
          v-for="channel in commsStore.channels"
          :key="channel.channel_id"
          type="button"
          class="flex items-center gap-1.5 border border-border rounded px-2 py-0.5 hover:bg-surface-container-high transition-colors cursor-pointer"
          @click="openEditDialog(channel)"
        >
          <span
            class="w-1.5 h-1.5 rounded-full shrink-0"
            :class="ASSIGNMENT_DOT[channel.assignment] ?? 'bg-outline'"
          />
          <span class="font-mono text-xs font-bold text-on-surface">{{ channel.name }}</span>
          <span
            class="font-mono text-[9px]"
            :class="ASSIGNMENT_TEXT[channel.assignment] ?? 'text-outline'"
          >{{ channel.assignment }}</span>
        </button>
      </div>

      <p v-else class="font-mono text-[10px] text-outline/50">No channels configured</p>
    </div>

    <!-- Divider -->
    <div class="border-l border-border self-stretch" />

    <!-- ICP Phone -->
    <div class="min-w-[150px]">
      <p class="text-[9px] font-label uppercase tracking-widest text-outline mb-1.5">ICP Phone</p>
      <input
        ref="phoneInputRef"
        type="tel"
        :value="opsStore.selectedOp?.contact_phone ?? ''"
        placeholder="None set"
        class="w-full font-mono text-xs text-on-surface bg-transparent border border-transparent rounded px-1.5 py-0.5 hover:border-border focus:border-border focus:bg-surface-container-high focus:outline-none focus:ring-1 focus:ring-primary/30 placeholder:text-outline/40 transition-colors cursor-pointer focus:cursor-text"
        @input="onPhoneInput"
        @blur="savePhone"
        @keydown.enter="phoneInputRef?.blur()"
      />
    </div>
  </div>

  <!-- Add dialog -->
  <FosDialog
    :open="addDialogOpen"
    :title="addDialogTitle"
    max-width="max-w-lg"
    @update:open="(v) => { if (!v) closeAddDialog() }"
  >
    <template v-if="!addShowCustomForm">
      <!-- Assignment selector -->
      <div class="flex items-center gap-2 mb-3">
        <span class="font-mono text-[0.65rem] uppercase tracking-widest text-outline shrink-0">Assignment</span>
        <FosSelect
          v-model="addAssignment"
          :options="ASSIGNMENTS"
          trigger-class="flex items-center justify-between gap-1 font-mono text-xs px-2 py-1 border border-border rounded bg-surface-container-high focus:outline-none focus:ring-1 focus:ring-primary/30 min-w-[140px]"
        />
      </div>

      <!-- Preset list -->
      <div class="flex flex-col -mx-6 -mb-2">
        <template v-if="presetsByMode.length > 0">
          <div class="flex divide-x divide-border border-y border-border">
            <div
              v-for="group in presetsByMode"
              :key="group.mode"
              class="flex-1 flex flex-col min-w-0"
            >
              <div class="px-3 py-1.5 bg-surface-container-lowest border-b border-border">
                <span class="font-mono text-[0.6rem] font-bold uppercase tracking-widest text-outline">{{ group.label }}</span>
              </div>
              <div
                v-for="preset in group.presets"
                :key="preset.preset_id"
                class="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-surface-container-high transition-colors border-b border-border"
                @click="addFromPreset(preset)"
              >
                <div class="flex-1 min-w-0">
                  <div class="font-mono text-xs font-bold text-on-surface truncate">{{ preset.name }}</div>
                  <div v-if="preset.rx_freq || preset.tx_freq" class="font-mono text-[0.6rem] text-outline mt-0.5">
                    <span v-if="preset.rx_freq">RX {{ preset.rx_freq }}</span>
                    <span v-if="preset.rx_freq && preset.tx_freq"> / </span>
                    <span v-if="preset.tx_freq">TX {{ preset.tx_freq }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <div v-else class="px-6 py-3 font-mono text-xs text-outline border-t border-border">
          No agency presets available
        </div>
        <button
          type="button"
          class="w-full text-left flex items-center gap-2 px-6 py-2.5 font-mono text-xs uppercase tracking-widest text-primary hover:bg-surface-container-high transition-colors font-bold"
          @click="openAddCustomForm"
        >
          <span class="material-symbols-outlined text-[16px]">add</span>
          Custom channel…
        </button>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-col gap-3">
        <div class="grid grid-cols-2 gap-3">
          <div class="col-span-2">
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Channel / System Name *</label>
            <FosInput v-model="addForm.name" placeholder="e.g. VTAC36R" />
          </div>
          <div>
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Mode</label>
            <FosSelect
              :model-value="addForm.mode"
              :options="MODE_OPTIONS"
              @update:model-value="addForm.mode = $event"
            />
          </div>
          <div>
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Freq</label>
            <FosInput v-model="addForm.rx_freq" placeholder="e.g. 155.340" />
          </div>
          <div>
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Tone/NAC</label>
            <FosInput v-model="addForm.rx_tone" placeholder="e.g. 100.0" />
          </div>
          <div>
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Freq</label>
            <FosInput v-model="addForm.tx_freq" placeholder="e.g. 155.340" />
          </div>
          <div>
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Tone/NAC</label>
            <FosInput v-model="addForm.tx_tone" placeholder="e.g. 100.0" />
          </div>
          <div class="col-span-2">
            <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Remarks</label>
            <FosInput v-model="addForm.remarks" placeholder="Optional notes" />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <template v-if="!addShowCustomForm">
        <FosButton variant="secondary" size="sm" @click="closeAddDialog">Cancel</FosButton>
      </template>
      <template v-else>
        <FosButton variant="secondary" size="sm" @click="addShowCustomForm = false">Back</FosButton>
        <FosButton size="sm" :disabled="!addForm.name.trim()" @click="submitAddCustom">Add Channel</FosButton>
      </template>
    </template>
  </FosDialog>

  <!-- Edit dialog -->
  <FosDialog
    :open="editDialogOpen"
    title="Edit channel"
    max-width="max-w-lg"
    @update:open="(v) => { if (!v) closeEditDialog() }"
  >
    <div class="flex flex-col gap-3">
      <div class="grid grid-cols-2 gap-3">
        <div class="col-span-2">
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Channel / System Name *</label>
          <FosInput v-model="editForm.name" placeholder="e.g. VTAC36R" />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Assignment</label>
          <FosSelect
            v-model="editForm.assignment"
            :options="ASSIGNMENTS"
          />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Mode</label>
          <FosSelect
            :model-value="editForm.mode"
            :options="MODE_OPTIONS"
            @update:model-value="editForm.mode = $event"
          />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Freq</label>
          <FosInput v-model="editForm.rx_freq" placeholder="e.g. 155.340" />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">RX Tone/NAC</label>
          <FosInput v-model="editForm.rx_tone" placeholder="e.g. 100.0" />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Freq</label>
          <FosInput v-model="editForm.tx_freq" placeholder="e.g. 155.340" />
        </div>
        <div>
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">TX Tone/NAC</label>
          <FosInput v-model="editForm.tx_tone" placeholder="e.g. 100.0" />
        </div>
        <div class="col-span-2">
          <label class="font-mono text-[0.65rem] uppercase tracking-widest text-outline mb-1 block">Remarks</label>
          <FosInput v-model="editForm.remarks" placeholder="Optional notes" />
        </div>
      </div>
    </div>

    <template #footer>
      <button
        type="button"
        class="inline-flex items-center justify-center font-mono text-[0.65rem] uppercase tracking-widest font-bold px-4 py-1 text-error hover:bg-error/5 rounded mr-auto"
        @click="deleteEditChannel"
      >Delete</button>
      <FosButton variant="secondary" size="sm" @click="closeEditDialog">Cancel</FosButton>
      <FosButton size="sm" :disabled="!editForm.name.trim()" @click="saveEdit">Save</FosButton>
    </template>
  </FosDialog>
</template>
